import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db, storage } from '../../firebase.js';
import MainLayoutV2 from '../../layouts/MainLayoutV2.jsx';
import { Typography, Button, TextField, Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, Tabs, Tab, Grid, ListItem, ListItemText, ListItemButton, Box, Divider, List, FormHelperText, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import Editor from '@monaco-editor/react'
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { CloudUpload as CloudUploadIcon, StarOutlined, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import Blocks from '../../stories/layout-components/Block.jsx';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import FieldEditor from '../../components/@generalComponents/FieldEditor.jsx';
import nodeAxiosFirebase from '../../utils/nodeAxiosFirebase.js';

// Define moduleTypes outside the component for stability
const moduleTypes = [
  { value: 'feature', labelKey: 'feature', descKey: 'featureDesc' },
  { value: 'integration', labelKey: 'integration', descKey: 'integrationDesc' },
  { value: 'plugin', labelKey: 'plugin', descKey: 'pluginDesc' },
  { value: 'account', labelKey: 'account', descKey: 'accountDesc' },
  { value: 'component', labelKey: 'component', descKey: 'componentDesc' },
  { value: 'template', labelKey: 'template', descKey: 'templateDesc' },
];

// Types that require code editors
const codeEditorTypes = ['feature', 'component', 'template', 'plugin', 'integration'];

const MarketPlaceNode = () => {
  const { t } = useTranslation();
  const [listing, setListing] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [structures, setStructures] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedStructures, setSelectedStructures] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [codeValues, setCodeValues] = useState({});
  const [pendingDeletionInfo, setPendingDeletionInfo] = useState(null);
  const [selectedForDeletion, setSelectedForDeletion] = useState([]);
  const [formValues, setFormValues] = useState({
    title: '',
    description: '',
    type: {},
    isActive: false,
    logoUrl: null,
    bestSeller: false,
    author: '',
    votes: 0,
    rating: 0,
    ownerId: '',
    requiredParams: [],
    collectionFields: [],
    moduleColor: '#000000',
    passwordRequired: false,
    permissions: [],
    basePrice: 0,
    monthlyFee: 0,
    feePerElement: false,
    releaseDate: null,
    fields: {},
    triggers: {
      manual: false,
      onCreate: false,
      onUpdate: false,
      onDelete: false,
      inboundCatch: false,
    },
  });
  const [imageUpload, setImageUpload] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [activeNodeScriptTab, setActiveNodeScriptTab] = useState(null); // State for sub-tabs


  const [availableParams, setAvailableParams] = useState([
    { id: 'apiKey', name: 'Api Key' },
    { id: 'moduleId', name: 'Module ID' },
    { id: 'structureId', name: 'Structure ID' },
    { id: 'key', name: 'API key' },
    { id: 'action', name: 'Action' },
    { id: 'openElement', name: 'Open Element' },
    { id: 'query', name: 'Query' },
    { id: 'location', name: 'Location' },
    { id: 'limit', name: 'Limit' },
    { id: 'language', name: 'Language' },
  ]);
  const [selectedParams, setSelectedParams] = useState([]);


  const [permissionOptions, setPermissionOptions] = useState([
    { value: 'readStructure', label: t('readStructure') },
    { value: 'readModule', label: t('readModule') },
    { value: 'readEmployees', label: t('readEmployees') },
    { value: 'readUsage', label: t('readUsage') },
    { value: 'businessData', label: t('businessData') },
    { value: 'createElement', label: t('createElement') },
    { value: 'updateElement', label: t('updateElement') },
    { value: 'deleteElement', label: t('deleteElement') },
    { value: 'readElement', label: t('readElement') },
    { value: 'createEmail', label: t('createEmail') },
    { value: 'updateEmail', label: t('updateEmail') },
    { value: 'createLog', label: t('createLog') },
    { value: 'createPDF', label: t('createPDF') },
    { value: 'updatePDF', label: t('updatePDF') },
    { value: 'createCell', label: t('createCell') },
  ]);


  const handleParamMove = (param, direction) => {
    if (direction === 'add') {
      setSelectedParams(prev => [...prev, param]);
      setAvailableParams(prev => prev.filter(p => p.id !== param.id));
    } else if (direction === 'remove') {
      setAvailableParams(prev => [...prev, param]);
      setSelectedParams(prev => prev.filter(p => p.id !== param.id));
    }
  };

  const moduleTypesLabels = moduleTypes.map(mt => ({
    ...mt,
    label: t(mt.labelKey),
    desc: t(mt.descKey)
  }));

  const collectionFieldOptions = [
    { value: 'contacts', label: t('contacts') },
    { value: 'cardsinvoiced', label: t('cardsinvoiced') },
    { value: 'cardsuninvoiced', label: t('cardsuninvoiced') },
    { value: 'passes', label: t('passes') },
    { value: 'services', label: t('services') },
    { value: 'grids', label: t('grids') },
    { value: 'articles', label: t('articles') },
    { value: 'profiles', label: t('profiles') },
  ];

  const handleGetData = async (ownerId) => {
    try {
      const nodeStructureData = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `business/admin`,
        errorToast: t('errorLoadingDocument'),
        body: {
          type: 'structures',
          businessId: ownerId || formValues?.ownerId,
        },
      });
      const formattedStructures = nodeStructureData?.data?.map((structure) => ({
        id: structure.id,
        value: structure.id,
        label: structure.name_fr,
        ownerId: ownerId || formValues?.ownerId,
        ...structure
      }));
      setStructures(formattedStructures);


      const nodeModuleData = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `business/admin`,
        errorToast: t('errorLoadingDocument'),
        body: {
          type: 'modules',
          businessId: ownerId || formValues?.ownerId,
        },
      });

      const formattedModules = nodeModuleData?.data?.map((module) => ({
        id: module.id,
        value: module.id,
        label: module.name_fr,
        ownerId: ownerId || formValues?.ownerId,
        ...module
      }));
      setModules(formattedModules);
    } catch (error) {
      console.error('Error getting node data:', error);
    }
  };

  useEffect(() => {
    const q = query(collection(db, 'marketplace'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const moduleList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setListing(moduleList);
    });
    return () => unsubscribe();
  }, []);



  // Handle Form Changes
  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;

    if (name.startsWith('trigger_')) {
      const triggerName = name.replace('trigger_', '');
      setFormValues({
        ...formValues,
        triggers: {
          ...formValues.triggers,
          [triggerName]: checked,
        },
      });
    } else if (type === 'checkbox') {
      setFormValues({
        ...formValues,
        [name]: checked,
      });
    }
    else {
      setFormValues({
        ...formValues,
        [name]: value,
      });
    }
  };

  // Default structure for a new feature code entry
  const defaultFeatureStructure = {
    id: '',
    title: '',
    initialState: '{}',
    uiRenderer: `// Use state, setState, handlers, t, data, localState, setLocalState, React, and common MUI Components passed in scope
<MainLayoutV2 icon={data?.logoUrl || ''} pageTitle={title || ''} subTitle={frontEndCode?.title || t('extensionNode')}>
  <Blocks height={1} heightPercentage={95}>
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Your JSX here */}
      <Typography>Edit uiRenderer for Feature: {frontEndCode?.title}</Typography>
      <TextField label="Example State Field" value={state?.exampleField || ''} onChange={handlers?.handleExampleChange} />
    </Box>
  </Blocks>
</MainLayoutV2>`,
    eventHandlers: `{
  // Example: handleExampleChange: (event) => setState(prev => ({...prev, exampleField: event.target.value}))
}`,
    effects: `[
  // Example: { effect: () => { console.log('Feature Mounted with state:', state); return () => console.log('Feature Unmounted'); }; deps: [] }
]`
  };


  // Handle Module Type Changes (Checkbox and Count)
  const handleTypeChange = (typeValue, checked, countInput) => {
    const currentCodeArray = codeValues[typeValue] || [];
    const currentCount = formValues.type?.[typeValue] || 0;
    let newCount = parseInt(countInput, 10);

    if (typeValue === 'account') {
      newCount = checked ? 1 : 0; // Enforce limit for 'account' type
    } else {
      // For other types, ensure count is valid or 0 if unchecked
      newCount = checked ? Math.max(1, isNaN(newCount) ? 1 : newCount) : 0;
    }

    const countDifference = newCount - currentCount;

    if (countDifference > 0 || (checked && currentCount === 0)) { // Count increased or type newly checked
      // Update formValues.type immediately
      setFormValues(prev => ({ ...prev, type: { ...prev.type, [typeValue]: newCount } }));

      // Add new code entries if it's a code editor type
      if (codeEditorTypes.includes(typeValue)) {
        setCodeValues(prevCodeValues => {
          const newCodeValues = { ...prevCodeValues };
          const existingCodes = newCodeValues[typeValue] || [];
          const newEntries = Array.from({ length: countDifference }, (_, i) => {
            const baseEntry = { id: `temp_${Date.now()}_${existingCodes.length + i}` };
            if (typeValue === 'feature') {
              // For features, store the structured object
              return { ...defaultFeatureStructure, ...baseEntry };
            } else {
              // For other types, store the old {title, code} structure
              return { ...baseEntry, title: '', code: '' };
            }
          });
          newCodeValues[typeValue] = [...existingCodes, ...newEntries];
          return newCodeValues;
        });
      }
    } else if (countDifference < 0 || (!checked && currentCount > 0)) { // Count decreased or type unchecked
      if (!checked) { // Type unchecked - remove all directly
        setFormValues(prev => {
          const newTypes = { ...prev.type };
          delete newTypes[typeValue];
          // Handle account-specific field clearing if needed
          const updates = typeValue === 'account' ? { ownerId: '', passwordRequired: false } : {};
          return { ...prev, ...updates, type: newTypes };
        });
        // Remove code entries if it's a code editor type
        if (codeEditorTypes.includes(typeValue)) {
          setCodeValues(prevCodeValues => {
            const newCodeValues = { ...prevCodeValues };
            delete newCodeValues[typeValue];
            return newCodeValues;
          });
        }
      } else { // Count decreased but still checked > 0
        // Only trigger confirmation for code editor types
        if (codeEditorTypes.includes(typeValue)) {
          // Trigger confirmation dialog
          setPendingDeletionInfo({
            typeValue,
            countToRemove: Math.abs(countDifference),
            candidates: currentCodeArray.map(c => ({ id: c.id, title: c.title || `Untitled #${c.id.slice(-4)}` })), // Send only id and title
            newCount: newCount // Store the target count
          });
          setSelectedForDeletion([]); // Reset selection
          // DO NOT update formValues.type or codeValues yet
        } else {
          // If not a code type, just update the count directly
          setFormValues(prev => ({ ...prev, type: { ...prev.type, [typeValue]: newCount } }));
        }
      }
    }
    // If countDifference === 0, do nothing to types or codeValues (unless it's just a title/code change handled elsewhere)
  };

  // Handler for changes in any code editor title, content, or structured field
  const handleCodeChange = (typeValue, index, field, newValue) => {
    setCodeValues(prev => {
      const updatedTypeCodes = [...(prev[typeValue] || [])];
      // Ensure the object exists at the index
      if (!updatedTypeCodes[index]) {
        // If it doesn't exist (shouldn't normally happen), create a default structure
        updatedTypeCodes[index] = typeValue === 'feature'
          ? { ...defaultFeatureStructure, id: `err_${Date.now()}_${index}` }
          : { id: `err_${Date.now()}_${index}`, title: '', code: '' }; // Old structure for other types
      }

      // Update the specific field (title, code, initialState, uiRenderer, etc.)
      updatedTypeCodes[index] = {
        ...updatedTypeCodes[index],
        [field]: newValue
      };

      return {
        ...prev,
        [typeValue]: updatedTypeCodes,
      };
    });
  };


  // Handle Image Upload
  const handleImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const img = event.target.files[0];
      setImageUpload(img);
      setLogoPreview(URL.createObjectURL(img));
    }
  };


  // Handle Release Date Change
  const handleReleaseDateChange = (date) => {
    setFormValues({ ...formValues, releaseDate: date ? date.toDate() : null });
  };


  // Handle Add/Edit Dialog Open
  const handleOpenDialog = (module = null) => {
    setSelectedModule(module);
    if (module) {
      const initialFormValues = {
        title: module.title || '',
        description: module.description || '',
        type: typeof module.type === 'string' ? { [module.type]: 1 } : (module.type || {}),
        isActive: module.isActive || false,
        logoUrl: module.logoUrl || null,
        bestSeller: module?.bestSeller || false,
        author: module.author || '',
        votes: module.votes || 0,
        rating: module.rating || 0,
        ownerId: module?.ownerId?.id || '',
        requiredParams: module.requiredParams || [],
        collectionFields: module.collectionFields || [],
        moduleColor: module.moduleColor || '#000000',
        passwordRequired: module.passwordRequired || false,
        permissions: module.permissions || [],
        basePrice: module.basePrice || 0,
        monthlyFee: module.monthlyFee || 0,
        feePerElement: module.feePerElement || false,
        perElementPrice: module.perElementPrice || 0, // Load perElementPrice
        perElementPriceSuffix: module.perElementPriceSuffix || '', // Load suffix
        releaseDate: module.releaseDate?.seconds ? new Date(module.releaseDate.seconds * 1000) : null,
        fields: module.fields || {},
        triggers: module.triggers || {
          manual: false,
          onCreate: false,
          onUpdate: false,
          onDelete: false,
          inboundCatch: false,
        },
      };
      setFormValues(initialFormValues);

      // Load existing code values, attempting to parse into appropriate format
      const loadedCode = module.code || {};
      const formattedCodeValues = {};
      for (const typeKey in loadedCode) {
        if (Array.isArray(loadedCode[typeKey])) {
          formattedCodeValues[typeKey] = loadedCode[typeKey].map((item, index) => {
            const baseId = item?.id || `node_${Date.now()}_${index}`;

            if (typeKey === 'feature') {
              // If it looks like the new structured format, use it
              if (typeof item === 'object' && item !== null && ('uiRenderer' in item || 'initialState' in item || 'eventHandlers' in item || 'effects' in item)) {
                return { ...defaultFeatureStructure, ...item, id: baseId };
              }
              // If it looks like the old {title, code} format, migrate code to uiRenderer
              else if (typeof item === 'object' && item !== null && 'code' in item) {
                return {
                  ...defaultFeatureStructure,
                  id: baseId,
                  title: item.title || '',
                  uiRenderer: item.code || defaultFeatureStructure.uiRenderer, // Migrate code
                };
              }
              // If it's just a string, treat it as the uiRenderer
              else if (typeof item === 'string') {
                return { ...defaultFeatureStructure, id: baseId, uiRenderer: item };
              }
              // Fallback: return default structure if format is unexpected
              return { ...defaultFeatureStructure, id: baseId, title: item?.title || '' };
            }
            // Handle non-feature types (expecting {id, title, code} or just string)
            else {
              if (typeof item === 'string') {
                return { id: baseId, title: '', code: item };
              }
              else if (typeof item === 'object' && item !== null) {
                return { id: baseId, title: item.title || '', code: item.code || '' };
              }
              // Fallback for non-feature types
              return { id: baseId, title: '', code: '' };
            }
          });
        }
      }
      setCodeValues(formattedCodeValues);

      setLogoPreview(module.logoUrl);
      setSelectedParams(module.requiredParams || []);

      setAvailableParams(prev => {
        const currentlyAvailable = [
          { id: 'apiKey', name: 'Api Key' }, { id: 'moduleId', name: 'Module ID' }, { id: 'structureId', name: 'Structure ID' },
          { id: 'key', name: 'API key' }, { id: 'action', name: 'Action' }, { id: 'openElement', name: 'Open Element' },
          { id: 'query', name: 'Query' }, { id: 'location', name: 'Location' }, { id: 'limit', name: 'Limit' }, { id: 'language', name: 'Language' },
        ];
        const selectedIds = (module.requiredParams || []).map(p => p.id);
        return currentlyAvailable.filter(p => !selectedIds.includes(p.id));
      });
      setSelectedStructures(module.structures || []);
      setSelectedModules(module.modules || []);

      if (module?.ownerId?.id) {
        handleGetData(module?.ownerId?.id);
      } else {
        setStructures([]);
        setModules([]);
      }
    } else {
      // Resetting for a new module
      const initialFormValues = {
        title: '',
        description: '',
        type: {},
        isActive: false,
        logoUrl: null,
        bestSeller: false,
        author: '',
        votes: 0,
        rating: 0,
        ownerId: '',
        requiredParams: [],
        collectionFields: [],
        moduleColor: '#000000',
        passwordRequired: false,
        permissions: [],
        basePrice: 0,
        monthlyFee: 0,
        feePerElement: false,
        perElementPrice: 0,
        perElementPriceSuffix: '',
        releaseDate: null,
        fields: {},
        triggers: {
          manual: false,
          onCreate: false,
          onUpdate: false,
          onDelete: false,
          inboundCatch: false,
        },
      };
      setFormValues(initialFormValues);
      setCodeValues({});
      setLogoPreview(null);
      setImageUpload(null);
      setSelectedParams([]);

      setAvailableParams([
        { id: 'apiKey', name: 'Api Key' }, { id: 'moduleId', name: 'Module ID' }, { id: 'structureId', name: 'Structure ID' },
        { id: 'key', name: 'API key' }, { id: 'action', name: 'Action' }, { id: 'openElement', name: 'Open Element' },
        { id: 'query', name: 'Query' }, { id: 'location', name: 'Location' }, { id: 'limit', name: 'Limit' }, { id: 'language', name: 'Language' },
      ]);
      setSelectedStructures([]);
      setSelectedModules([]);

      setStructures([]);
      setModules([]);
    }
    setActiveTab(0);
    setActiveNodeScriptTab(null); // Reset sub-tab on dialog open
    setOpenDialog(true);
  };

  // Handle Dialog Close
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedModule(null);
    setFormValues({
      title: '', description: '', type: {}, isActive: false, logoUrl: null,
      bestSeller: false, author: '', votes: 0, rating: 0, ownerId: '',
      requiredParams: [], collectionFields: [], moduleColor: '#000000',
      passwordRequired: false, permissions: [], basePrice: 0, monthlyFee: 0,
      feePerElement: false, perElementPrice: 0, perElementPriceSuffix: '',
      releaseDate: null, fields: {},
      triggers: { manual: false, onCreate: false, onUpdate: false, onDelete: false, inboundCatch: false },
    });
    setImageUpload(null);
    setLogoPreview(null);
    setSelectedParams([]);
    setAvailableParams([
      { id: 'apiKey', name: 'Api Key' }, { id: 'moduleId', name: 'Module ID' }, { id: 'structureId', name: 'Structure ID' },
      { id: 'key', name: 'API key' }, { id: 'action', name: 'Action' }, { id: 'openElement', name: 'Open Element' },
      { id: 'query', name: 'Query' }, { id: 'location', name: 'Location' }, { id: 'limit', name: 'Limit' }, { id: 'language', name: 'Language' },
    ]);

    setSelectedStructures([]);
    setSelectedModules([]);
    setCodeValues({});
    setStructures([]);
    setModules([]);
    setActiveNodeScriptTab(null); // Reset sub-tab on close
  };

  const handleSaveModule = async () => {
    setIsLoading(true);

    // Validate that at least one type is selected and has a count > 0
    const selectedTypes = Object.entries(formValues.type || {}).filter(([key, value]) => value > 0);
    if (selectedTypes.length === 0) {
      alert(t('selectAtLeastOneModuleType'));
      setIsLoading(false);
      setActiveTab(0);
      return;
    }

    // Basic title validation
    if (!formValues.title?.trim()) {
      alert(t('titleRequired'));
      setIsLoading(false);
      setActiveTab(0);
      return;
    }


    try {
      let logoUrl = formValues.logoUrl;
      if (imageUpload) {
        const imageRef = ref(storage, `marketplace_logos/${Date.now()}_${imageUpload.name}`);
        const uploadResult = await uploadBytes(imageRef, imageUpload);
        logoUrl = await getDownloadURL(uploadResult.ref);
      }

      let ownerRef = null;
      if (formValues?.ownerId) {
        try {
          const ownerIdString = typeof formValues.ownerId === 'string' ? formValues.ownerId.trim() : '';
          if (ownerIdString) {
            ownerRef = doc(db, 'businessesOnNode', ownerIdString);
          }
        } catch (e) {
          console.error("Invalid ownerId format:", formValues.ownerId, e);
          alert(t('invalidOwnerIdFormat'));
          setIsLoading(false);
          setActiveTab(0);
          return;
        }
      }

      const currentVersion = selectedModule ? (selectedModule?.version || 0) : 0;
      const newVersion = currentVersion + 1;

      // Prepare code data: features store the structured object, others store {title, code}
      // No generation happens here anymore.
      const processedCodeValues = JSON.parse(JSON.stringify(codeValues)); // Deep clone

      // Basic validation for feature structure before saving (optional but recommended)
      if (processedCodeValues.feature) {
        for (const featureInstance of processedCodeValues.feature) {
          // Example validation: Check if required fields exist
          if (!featureInstance.uiRenderer || !featureInstance.initialState || !featureInstance.eventHandlers || !featureInstance.effects) {
            console.warn("Feature instance is missing structured fields:", featureInstance);
            // Decide if you want to alert the user or handle differently
          }
          // Remove the 'code' field if it accidentally exists from old versions
          delete featureInstance.code;
        }
      }

      const dataToSave = {
        title: formValues.title,
        description: formValues.description,
        type: formValues.type || {},
        isActive: formValues.isActive || false,
        bestSeller: formValues.bestSeller || false,
        author: formValues.author,
        votes: parseInt(formValues?.votes || 0),
        rating: parseFloat(formValues?.rating || 0),
        basePrice: parseFloat(formValues?.basePrice || 0),
        monthlyFee: parseFloat(formValues?.monthlyFee || 0),
        feePerElement: formValues.feePerElement || false,
        perElementPrice: formValues.feePerElement ? parseFloat(formValues.perElementPrice || 0) : 0,
        perElementPriceSuffix: formValues.feePerElement ? (formValues.perElementPriceSuffix || '') : '',
        ownerId: ownerRef,
        logoUrl: logoUrl,
        moduleColor: formValues.moduleColor || '#000000',
        passwordRequired: formValues?.passwordRequired || false,
        requiredParams: selectedParams || [],
        lastUpdate: serverTimestamp(),
        releaseDate: formValues?.releaseDate instanceof Date ? formValues.releaseDate : null,
        fields: formValues?.fields || {},
        triggers: formValues?.triggers || {},
        structures: selectedStructures || [],
        modules: selectedModules || [],
        code: processedCodeValues, // Save the structured data for features
        collectionFields: formValues.collectionFields || [],
        permissions: formValues.permissions || [],
        status: formValues.status || 'draft',
        version: newVersion,
      };


      if (selectedModule) {
        await updateDoc(doc(db, 'marketplace', selectedModule.id), dataToSave);
      } else {
        dataToSave.timeStamp = serverTimestamp();
        await addDoc(collection(db, 'marketplace'), dataToSave);
      }
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving module: ", error);
      alert(t('errorSavingModule') + `: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };


  const handleDeleteModule = async (moduleId) => {
    if (window.confirm(t('confirmDeleteModule'))) {
      try {
        await deleteDoc(doc(db, 'marketplace', moduleId));
      } catch (error) {
        console.error("Error deleting module: ", error);
        alert(t('errorDeletingModule') + `: ${error.message}`);
      }
    }
  };

  const handleTabChange = (event, newTab) => {
    setActiveTab(newTab);
  };

  // Simplified useEffect for account type changes and data fetching
  useEffect(() => {
    const isAccountTypeSelected = formValues.type?.account > 0;
    const hasOwnerId = !!formValues.ownerId?.trim();

    // Clear related fields if account type is deselected
    if (!isAccountTypeSelected && (formValues.ownerId || formValues.passwordRequired)) {
      setFormValues(prev => ({
        ...prev,
        ownerId: '',
        passwordRequired: false
      }));
      // Clear structures/modules if ownerId is cleared due to type change
      setStructures([]);
      setModules([]);
      setSelectedStructures([]);
      setSelectedModules([]);
    }

    // Fetch data if account type is selected, ownerId is present, and data hasn't been fetched yet
    if (isAccountTypeSelected && hasOwnerId && (!structures.length || !modules.length)) {
      handleGetData(formValues.ownerId);
    }

    // Clear structures/modules if ownerId is removed/emptied while account type is selected
    if (isAccountTypeSelected && !hasOwnerId && (structures.length > 0 || modules.length > 0)) {
      setStructures([]);
      setModules([]);
      setSelectedStructures([]);
      setSelectedModules([]);
    }

  }, [formValues.type?.account, formValues.ownerId]); // Dependencies


  const handleSelectStructure = (value) => {
    // value is expected to be an array of IDs
    const formattedValue = value.map((itemId) => {
      const matchedStructure = structures.find((structure) => structure.id === itemId);
      return {
        id: itemId,
        collectionField: matchedStructure?.collectionField || '', // Ensure default value
        name_fr: matchedStructure?.name_fr || '',
        name_en: matchedStructure?.name_en || '',
      }
    })

    setSelectedStructures(formattedValue);
  };

  const handleSelectModule = (value) => {
    // value is expected to be an array of IDs
    const formattedValue = value.map((itemId) => {
      const matchedModule = modules.find((module) => module.id === itemId);
      return {
        id: itemId,
        collectionField: matchedModule?.collectionField || '', // Ensure default value
        name_fr: matchedModule?.name_fr || '',
        name_en: matchedModule?.name_en || '',
      }
    })

    setSelectedModules(formattedValue);
  }

  const handleStructureFieldsChange = (structureId, newFields) => {
    setFormValues(prevFormValues => {
      const newFieldsObject = { ...prevFormValues.fields };
      newFieldsObject[structureId] = newFields;
      return { ...prevFormValues, fields: newFieldsObject };
    });
  };

  // Initialize structure fields when structures are selected/loaded
  useEffect(() => {
    // Ensure structures are loaded and some are selected
    if (structures.length > 0 && selectedStructures.length > 0) {
      let fieldsUpdated = false;
      // Start with the existing fields from formValues or an empty object
      const updatedFields = { ...(formValues.fields || {}) };

      selectedStructures.forEach(structureData => {
        const structureId = structureData.id;
        // Check if fields for this structure ID are NOT already initialized OR if the structure itself changed potentially
        if (!updatedFields[structureId]) { // Simple check: initialize if not present
          const fullStructure = structures.find(s => s.id === structureId);
          // Check if the found structure actually has fields defined
          if (fullStructure?.fields && fullStructure.fields.length > 0) {
            // Map fields to the initial state: { id: fieldId, selected: false }
            const initialStructureFieldsState = fullStructure.fields.map(field => ({
              id: field.id,
              selected: false // Initially not selected
            }));
            // Assign the initial state to the structure ID key
            updatedFields[structureId] = initialStructureFieldsState;
            fieldsUpdated = true; // Mark that we made an update
          } else {
            // If no fields exist for this structure, initialize with an empty array
            updatedFields[structureId] = [];
            // Optionally set fieldsUpdated = true if initializing to empty array should trigger a state update
            // fieldsUpdated = true;
          }
        }
        // Potential enhancement: Could also check if existing fields in `updatedFields[structureId]`
        // match the fields in `fullStructure.fields` and update if they mismatch.
      });

      // If any fields were initialized or potentially updated, update the formValues state
      if (fieldsUpdated) {
        setFormValues(prev => ({ ...prev, fields: updatedFields }));
      }
    }

  }, [structures, selectedStructures]);

  // --- Deletion Confirmation Handlers ---

  const handleDeletionSelectionChange = (event, id) => {
    const { checked } = event.target;
    const currentSelectionCount = selectedForDeletion.length;
    const maxSelection = pendingDeletionInfo?.countToRemove || 0;

    if (checked) {
      if (currentSelectionCount < maxSelection) {
        setSelectedForDeletion(prev => [...prev, id]);
      } else {
        // Optional: Provide feedback that max selection is reached
        console.warn(`Cannot select more than ${maxSelection} item(s) for deletion.`);
        // Prevent checkbox from visually changing if limit is reached
        event.preventDefault();
      }
    } else {
      setSelectedForDeletion(prev => prev.filter(selectedId => selectedId !== id));
    }
  };


  const handleConfirmDeletion = () => {
    if (!pendingDeletionInfo || selectedForDeletion.length !== pendingDeletionInfo.countToRemove) {
      alert(`${t('pleaseSelectExactly')} ${pendingDeletionInfo?.countToRemove} ${t('item(s)ToDelete')}`);
      return; // Ensure the correct number is selected
    }

    const { typeValue, newCount } = pendingDeletionInfo;

    // Update codeValues by filtering out the selected IDs
    setCodeValues(prev => ({
      ...prev,
      [typeValue]: (prev[typeValue] || []).filter(editor => !selectedForDeletion.includes(editor.id))
    }));

    // Update formValues.type to the new count
    setFormValues(prev => ({
      ...prev,
      type: {
        ...prev.type,
        [typeValue]: newCount
      }
    }));

    // Clear pending deletion state
    setPendingDeletionInfo(null);
    setSelectedForDeletion([]);
  };

  const handleCancelDeletion = () => {
    setPendingDeletionInfo(null); // Just clear the state
    setSelectedForDeletion([]);
  };

  // Effect to manage the active NodeScript sub-tab
  useEffect(() => {
    if (activeTab === 6) {
      const activeCodeTypes = codeEditorTypes.filter(type => formValues.type?.[type] > 0);
      if (activeCodeTypes.length > 0) {
        // If the current sub-tab is no longer valid or not set, default to the first active type
        if (!activeNodeScriptTab || !activeCodeTypes.includes(activeNodeScriptTab)) {
          setActiveNodeScriptTab(activeCodeTypes[0]);
        }
      } else {
        setActiveNodeScriptTab(null); // No active code types, reset sub-tab
      }
    }
  }, [activeTab, formValues.type, activeNodeScriptTab]); // Added activeNodeScriptTab dependency

  const handleNodeScriptTabChange = (event, newValue) => {
    setActiveNodeScriptTab(newValue);
  };

  // Get active code editor types for rendering sub-tabs
  const activeCodeEditorTypes = codeEditorTypes.filter(type => formValues.type?.[type] > 0);


  return (
    <MainLayoutV2 pageTitle={t('marketplace')} subTitle={'Modules & Plugins'} actions={{ add: () => handleOpenDialog() }} icon='StorefrontOutlined'>
      <Blocks heightPercentage={98}>
        {listing?.length > 0 ? listing.map((module) => (
          <div key={module.id} style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '15px', margin: '10px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, maxWidth: '300px' }}> {/* Logo and Title */}
              <img src={module.logoUrl || '/img/placeholder.png'} // Consistent fallback
                alt={module.title}
                style={{ width: '40px', height: '40px', marginRight: '15px', objectFit: 'contain', border: '1px solid #eee', borderRadius: '4px' }}
                onError={(e) => { e.target.onerror = null; e.target.src = '/img/placeholder.png'; }}
              />
              <div>
                <Typography variant="h6" fontSize='1rem' fontWeight='500' noWrap title={module?.title} sx={{ mb: 0.5 }}>{module?.title || t('untitled')}</Typography>
                <Typography lineHeight='1.2' variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: '70px' }}>
                  {module.description || t('noDescription')}
                </Typography>
              </div>
            </div>

            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            <Box flexGrow={1} display="grid" gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))" gap={2}> {/* Grid for details */}
              <div>
                <Typography variant="caption" color="text.secondary" display="block">{t('type')}</Typography>
                {typeof module.type === 'object' && module.type !== null && Object.keys(module.type).length > 0 ? (
                  Object.entries(module.type).map(([key, count]) => (
                    <Typography key={key} variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {t(key)} ({count})
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{t(module.type || 'N/A')}</Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>{t('status')}</Typography>
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{t(module.status || 'draft')}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>{t('active')}</Typography>
                <Typography variant="body2">{module.isActive ? t('yes') : t('no')}</Typography>
              </div>
              <div>
                <Typography variant="caption" color="text.secondary" display="block">{t('rating')}</Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <StarOutlined sx={{ fontSize: '1rem', color: '#ffb400' }} />
                  <Typography variant="body2" fontWeight="500">{module?.rating?.toFixed(1) || '0.0'}</Typography>
                  <Typography variant="body2" color="text.secondary">({module?.votes || '0'})</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>{t('author')}</Typography>
                <Typography variant="body2">{module?.author || '-'}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>{t('releaseDate')}</Typography>
                <Typography variant="body2">{module.releaseDate?.seconds ? moment(module.releaseDate.seconds * 1000).format('YYYY-MM-DD') : '-'}</Typography>
              </div>
              <div>
                <Typography variant="caption" color="text.secondary" display="block">{t('data')}</Typography>
                <Typography variant="body2">{t('structures')}: {module.structures?.length || '0'}</Typography>
                <Typography variant="body2">{t('modules')}: {module.modules?.length || '0'}</Typography>
                <Typography variant="body2">{t('collections')}: {module.collectionFields?.length || '0'}</Typography>
                <Typography variant="body2">{t('codeSnippets')}: {module.code ? Object.entries(module.code).reduce((acc, [key, value]) => acc + (Array.isArray(value) ? value.length : 0), 0) : '0'}</Typography>
              </div>
              <div>
                <Typography variant="caption" color="text.secondary" display="block">{t('pricing')}</Typography>
                <Typography variant="body2">{t('basePrice')}: {module.basePrice != null ? `${module.basePrice} $` : '-'}</Typography>
                <Typography variant="body2">{t('monthlyFee')}: {module.monthlyFee != null ? `${module.monthlyFee} $` : '-'}</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{t('feePerElement')}: {module.feePerElement ? `${t('yes')} (${module.perElementPrice || 0}$/${module.perElementPriceSuffix || t('element')})` : t('no')}</Typography>
              </div>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px', flexShrink: 0 }}> {/* Buttons */}
              <Button onClick={() => handleOpenDialog(module)} disableElevation variant="contained" color="primary" size="small">
                {t('edit')}
              </Button>
              <Button onClick={() => handleDeleteModule(module.id)} variant="text" color="error" size="small">
                {t('delete')}
              </Button>
            </div>
          </div>
        )) : (
          <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>{t('noModulesFound')}</Typography>
        )}
      </Blocks>


      {/* Dialog for Add/Edit */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth> {/* Use lg for wider dialog */}
        <DialogTitle>{selectedModule ? t('editModule') : t('addModule')}</DialogTitle>
        <DialogContent sx={{ pb: 1 }}> {/* Reduce bottom padding */}

          <Box sx={{ borderBottom: 1, borderColor: 'divider', position: 'sticky', top: -24, /* Adjust based on DialogContent padding */ backgroundColor: 'background.paper', zIndex: 10, mx: -3, px: 3, pt: 1 }}>
            <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" aria-label="module edit tabs">
              {/* Define Tab structure for better mapping */}
              {[
                { label: t('general'), index: 0 },
                { label: t('parameters'), index: 1, disabled: !formValues?.title },
                { label: t('dataAccess'), index: 2, disabled: !formValues?.title || !(formValues.type?.account > 0) },
                { label: t('fields'), index: 3, disabled: !formValues?.title },
                { label: t('permissions'), index: 4, disabled: !formValues?.title },
                { label: t('pricing'), index: 5, disabled: !formValues?.title },
                { label: 'NodeScript', index: 6, disabled: !formValues?.title || !activeCodeEditorTypes.length }, // Disable if no active code types
              ].map(tab => (
                <Tab key={tab.index} label={tab.label} disabled={tab.disabled} sx={{ textTransform: 'none', minWidth: 'auto', px: 2 }} />
              ))}
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <Box sx={{ pt: 2 }}>
            {activeTab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label={t('title')} name="title" value={formValues.title} onChange={handleChange} fullWidth margin="dense" required autoFocus />
                </Grid>
                <Grid item xs={12} sm={6} md={8}>
                  <TextField label={t('description')} name="description" value={formValues.description} onChange={handleChange} fullWidth multiline rows={1} margin="dense" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 1, mb: 0 }}>{t('moduleTypes')}</Typography>
                  <Grid container spacing={1}>
                    {moduleTypesLabels.map((type) => {
                      const isChecked = !!formValues.type?.[type.value];
                      const count = formValues.type?.[type.value] || 1;
                      const isAccount = type.value === 'account';
                      return (
                        <Grid item xs={12} sm={6} md={4} key={type.value} sx={{ display: 'flex', alignItems: 'center' }}>
                          <FormControlLabel
                            control={<Checkbox checked={isChecked} onChange={(e) => handleTypeChange(type.value, e.target.checked, count)} name={`type_${type.value}`} />}
                            label={
                              <Box>
                                <Typography>{type.label}</Typography>
                                <Typography variant="caption" color="text.secondary">{type.desc}</Typography>
                              </Box>
                            }
                            sx={{ flexGrow: 1, mr: 1 }}
                          />
                          <TextField
                            type="number"
                            value={count}
                            onChange={(e) => handleTypeChange(type.value, isChecked, e.target.value)}
                            disabled={!isChecked || isAccount}
                            InputProps={{ inputProps: { min: 1 } }}
                            size="small" sx={{ width: '70px' }} margin="dense"
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', pt: 1 }}>
                    <FormControlLabel control={<Checkbox checked={formValues.isActive} onChange={handleChange} name="isActive" />} label={t('active')} sx={{ height: '30px' }} />
                    <FormControlLabel control={<Checkbox checked={formValues.bestSeller} onChange={handleChange} name="bestSeller" />} label={t('bestSeller')} sx={{ height: '30px' }} />
                    <FormControlLabel control={<Checkbox checked={formValues.passwordRequired} onChange={handleChange} name="passwordRequired" disabled={!(formValues.type?.account > 0)} />} label={t('passwordRequired')} sx={{ height: '30px' }} />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label={t('author')} name="author" value={formValues.author} onChange={handleChange} fullWidth margin="dense" />
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      label={t('releaseDate')}
                      value={formValues.releaseDate ? moment(formValues.releaseDate) : null}
                      onChange={handleReleaseDateChange}
                      slotProps={{ textField: { fullWidth: true, margin: "dense" } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label={t('votes')} name="votes" type="number" value={formValues.votes} onChange={handleChange} fullWidth margin="dense" InputProps={{ inputProps: { min: 0 } }} />
                  <TextField label={t('rating')} name="rating" type="number" value={formValues.rating} onChange={handleChange} fullWidth margin="dense" InputProps={{ inputProps: { min: 0, max: 5, step: 0.1 } }} />
                </Grid>
                {formValues?.type?.account > 0 && (
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField label={t('ownerId')} name="ownerId" value={formValues.ownerId} onChange={handleChange} fullWidth margin="dense" helperText={t('ownerIdHelper')} />
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" gutterBottom>{t('moduleColor')}</Typography>
                  <input type="color" value={formValues.moduleColor} onChange={(e) => setFormValues({ ...formValues, moduleColor: e.target.value })} style={{ width: '100%', height: '40px', border: '1px solid #ccc', borderRadius: '4px', padding: 0, cursor: 'pointer', backgroundColor: 'transparent' }} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} sx={{ alignSelf: 'flex-end' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} size="small">
                      {t('selectLogo')}
                      <input id="logo-upload" type="file" accept="image/*" onChange={handleImageChange} hidden />
                    </Button>
                    {logoPreview && <img src={logoPreview} alt="Logo Preview" style={{ width: '40px', height: '40px', objectFit: 'contain', border: '1px solid #eee', borderRadius: '4px' }} />}
                  </Box>
                </Grid>
              </Grid>
            )}
            {activeTab === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>{t('requiredParams')}</Typography>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={5}>
                      <Typography variant="caption" display="block" gutterBottom>{t('availableParams')}</Typography>
                      <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', p: 1, minHeight: '150px', maxHeight: '250px', overflowY: 'auto' }}>
                        {availableParams.length > 0 ? availableParams.map(param => (
                          <Box key={param.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px dashed #eee' }}>
                            <Typography variant="body2">{param.name}</Typography>
                            <Button size="small" onClick={() => handleParamMove(param, 'add')} variant="text">{t('add')}</Button>
                          </Box>
                        )) : <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>{t('noAvailableParams')}</Typography>}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={2} sx={{ textAlign: 'center', alignSelf: 'center', display: { xs: 'none', md: 'block' } }}>
                      <Typography>&harr;</Typography> {/* Horizontal arrow */}
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Typography variant="caption" display="block" gutterBottom>{t('selectedParams')}</Typography>
                      <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', p: 1, minHeight: '150px', maxHeight: '250px', overflowY: 'auto' }}>
                        {selectedParams.length > 0 ? selectedParams.map(param => (
                          <Box key={param.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px dashed #eee' }}>
                            <Typography variant="body2">{param.name}</Typography>
                            <Button size="small" onClick={() => handleParamMove(param, 'remove')} variant="text" color="error">{t('remove')}</Button>
                          </Box>
                        )) : <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>{t('noSelectedParams')}</Typography>}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>{t('triggers')}</Typography>
                  <Grid container spacing={1}>
                    {['manual', 'onCreate', 'onUpdate', 'onDelete', 'inboundCatch', 'onScheduled'].map(triggerKey => (
                      <Grid item xs={6} sm={4} md={2} key={triggerKey}>
                        <FormControlLabel control={<Checkbox checked={!!formValues.triggers[triggerKey]} onChange={handleChange} name={`trigger_${triggerKey}`} />} label={t(triggerKey)} />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>{t('collectionFields')}</Typography>
                  <Grid container spacing={1}>
                    {collectionFieldOptions.map(option => (
                      <Grid item xs={6} sm={4} md={3} key={option.value}>
                        <FormControlLabel
                          control={<Checkbox checked={formValues.collectionFields.includes(option.value)} onChange={(event) => { const { checked } = event.target; setFormValues(prev => ({ ...prev, collectionFields: checked ? [...prev.collectionFields, option.value] : prev.collectionFields.filter(field => field !== option.value) })); }} name="collectionFields" />}
                          label={option.label}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            )}
            {activeTab === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>{t('dataAccess')}</Typography>
                {!formValues.ownerId && <Typography color="text.secondary" sx={{ mb: 2 }}>{t('enterOwnerIdFirst')}</Typography>}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1">{t('structures')}</Typography>
                    <FormControl fullWidth margin="dense">
                      <InputLabel>{t('selectStructures')}</InputLabel>
                      <Select multiple value={selectedStructures?.map(s => s.id) || []} onChange={(e) => handleSelectStructure(e.target.value)}
                        renderValue={(selectedIds) => selectedIds.map(id => structures.find(st => st.id === id)?.label || id).join(', ')}
                        disabled={!formValues.ownerId || structures.length === 0}>
                        {structures.length > 0 ? structures.map((structure) => (<MenuItem key={structure.id} value={structure.id}>{structure.label}</MenuItem>)) : <MenuItem disabled>{t('loadingOrNoStructures')}</MenuItem>}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1">{t('modules')}</Typography>
                    <FormControl fullWidth margin="dense">
                      <InputLabel>{t('selectModules')}</InputLabel>
                      <Select multiple value={selectedModules?.map(m => m.id) || []} onChange={(e) => handleSelectModule(e.target.value)}
                        renderValue={(selectedIds) => selectedIds.map(id => modules.find(mo => mo.id === id)?.label || id).join(', ')}
                        disabled={!formValues.ownerId || modules.length === 0}>
                        {modules.length > 0 ? modules.map((module) => (<MenuItem key={module.id} value={module.id}>{module.label}</MenuItem>)) : <MenuItem disabled>{t('loadingOrNoModules')}</MenuItem>}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}
            {activeTab === 3 && (
              <Box>
                <Typography variant="h6" gutterBottom>{t('customFields')}</Typography>
                <FieldEditor fields={formValues?.fields?.['custom'] || []} setFields={(newFields) => setFormValues({ ...formValues, fields: { ...formValues.fields, custom: newFields } })} fromExtensions={formValues?.collectionFields} isExternal={formValues?.type?.integration > 0} />

                {formValues?.type?.account > 0 && selectedStructures?.length > 0 && (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ marginTop: '20px' }}>{t('fieldsStructures')}</Typography>
                    {selectedStructures.map((structureData) => {
                      const structureId = structureData.id;
                      const structure = structures.find((s) => s.id === structureId);
                      const structureFieldsForForm = formValues.fields?.[structureId] || [];
                      const allFieldsForStructure = structure?.fields || [];

                      return (
                        <Box key={structureId} sx={{ mb: 3, border: '1px solid #eee', p: 1, borderRadius: '4px' }}>
                          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>{structureData?.name_fr || structureId}</Typography>
                          {allFieldsForStructure.length > 0 ? (
                            <List dense sx={{ p: 0 }}>
                              {allFieldsForStructure.map((field) => {
                                const fieldState = structureFieldsForForm.find(f => f.id === field.id);
                                const isSelected = fieldState ? fieldState.selected : false;
                                return (
                                  <ListItem key={field?.id} divider sx={{ p: 0.5 }}>
                                    <Grid container alignItems="center" spacing={1}>
                                      <Grid item xs={4}><ListItemText primary={field?.name_fr} secondary={field.id} sx={{ m: 0, '& .MuiListItemText-secondary': { fontSize: '0.75rem' } }} /></Grid>
                                      <Grid item xs={2}><ListItemText primary={t(field?.typeData)} sx={{ m: 0 }} /></Grid>
                                      <Grid item xs={3}><ListItemText primary={`${field?.selections?.length || 0} ${t('options')}`} sx={{ m: 0 }} /></Grid>
                                      <Grid item xs={3} textAlign="right">
                                        <Button size="small" variant={isSelected ? "outlined" : "contained"} onClick={() => {
                                          const fieldIndex = structureFieldsForForm.findIndex(f => f.id === field.id);
                                          let updatedFields;
                                          if (fieldIndex > -1) { updatedFields = structureFieldsForForm.map((f, index) => index === fieldIndex ? { ...f, selected: !isSelected } : f); }
                                          else { updatedFields = [...structureFieldsForForm, { id: field.id, selected: true }]; }
                                          handleStructureFieldsChange(structureId, updatedFields);
                                        }}>
                                          {isSelected ? t('deselect') : t('select')}
                                        </Button>
                                      </Grid>
                                    </Grid>
                                  </ListItem>
                                );
                              })}
                            </List>
                          ) : (<Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>{t('noFieldsAvailableForStructure')}</Typography>)}
                        </Box>
                      );
                    })}
                  </>
                )}
              </Box>
            )}
            {activeTab === 4 && ( // Permissions Tab
              <Box>
                <Typography variant="h6" gutterBottom>{t('permissions')}</Typography>
                <Grid container spacing={1}>
                  {permissionOptions.map(permission => (
                    <Grid item xs={12} sm={6} md={4} key={permission.value}>
                      <FormControlLabel control={<Checkbox checked={formValues.permissions.includes(permission.value)} onChange={(event) => { const { checked } = event.target; setFormValues(prev => ({ ...prev, permissions: checked ? [...prev.permissions, permission.value] : prev.permissions.filter(p => p !== permission.value) })); }} name="permissions" />} label={permission?.label || permission.value} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            {activeTab === 5 && ( // Pricing Tab
              <Box>
                <Typography variant="h6" gutterBottom>{t('pricing')}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField label={t('basePrice')} name="basePrice" value={formValues.basePrice} onChange={handleChange} fullWidth margin="dense" type="number" InputProps={{ inputProps: { step: 0.01, min: 0 } }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label={t('monthlyFee')} name="monthlyFee" value={formValues.monthlyFee} onChange={handleChange} fullWidth margin="dense" type="number" InputProps={{ inputProps: { step: 0.01, min: 0 } }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                      <FormControlLabel control={<Checkbox checked={formValues.feePerElement} onChange={handleChange} name="feePerElement" />} label={t('feePerElement')} />
                      {formValues?.feePerElement && (
                        <>
                          <TextField label={t('perElementPrice')} name="perElementPrice" type="number" InputProps={{ inputProps: { step: 0.001, min: 0 } }} value={formValues.perElementPrice} onChange={handleChange} size="small" margin="dense" sx={{ width: '150px' }} />
                          <TextField label={t('perElementPriceSuffix')} name="perElementPriceSuffix" value={formValues.perElementPriceSuffix} onChange={handleChange} size="small" margin="dense" placeholder={t('e.g., item, user, call')} sx={{ width: '200px' }} />
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
            {activeTab === 6 && (
              <Box sx={{ overflow: 'hidden', maxHeight: 'calc(100vh - 300px)' /* Adjust height dynamically */ }}>
                {activeCodeEditorTypes.length > 0 ? (
                  <>
                    {/* Sub-Tabs for NodeScript Types */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, position: 'sticky', top: 0, backgroundColor: 'background.paper', zIndex: 9 }}>
                      <Tabs value={activeNodeScriptTab} onChange={handleNodeScriptTabChange} variant="scrollable" scrollButtons="auto" aria-label="nodescript type tabs">
                        {activeCodeEditorTypes.map(typeValue => (
                          <Tab key={typeValue} label={t(typeValue)} value={typeValue} sx={{ textTransform: 'none', minWidth: 'auto', px: 2 }} />
                        ))}
                      </Tabs>
                    </Box>


                    {activeNodeScriptTab && (
                      <Box sx={{ height: 'calc(100vh - 300px)', overflowY: 'auto', mt: 1 }}>
                        {Array.from({ length: formValues.type?.[activeNodeScriptTab] || 0 }).map((_, index) => {
                          const editorData = codeValues[activeNodeScriptTab]?.[index] ||
                            (activeNodeScriptTab === 'feature' ? defaultFeatureStructure : { id: `err_${Date.now()}_${index}`, title: '', code: '' });

                          // --- Feature Type Structured Editor ---
                          if (activeNodeScriptTab === 'feature') {
                            return (
                              <div className='row'><div className='col-9'> <Box key={editorData.id} sx={{ border: '1px solid #ddd', borderRadius: '4px', mb: 3, overflow: 'hidden' }}>
                                <TextField
                                  fullWidth variant="outlined" size="small"
                                  label={`${t('feature')} #${index + 1} ${t('title')}`}
                                  value={editorData.title || ''}
                                  onChange={(e) => handleCodeChange(activeNodeScriptTab, index, 'title', e.target.value)}
                                  sx={{ backgroundColor: '#f9f9f9', borderBottom: '1px solid #ddd', '& .MuiOutlinedInput-root': { borderRadius: '0', '& fieldset': { border: 'none' } } }}
                                />
                                <Box sx={{ p: 2 }}>
                                  {/* Initial State */}
                                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>{t('initialState')}</Typography>
                                  <FormHelperText sx={{ mt: -1, mb: 0.5 }}>{t('initialStateHelper')}</FormHelperText>
                                  <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', mb: 2, overflow: 'hidden' }}>
                                    <Editor
                                      height="150px" // Adjust height as needed
                                      width="100%" language="javascript" // Use json or javascript
                                      theme="vs-dark"
                                      value={editorData.initialState || ''}
                                      onChange={(value) => handleCodeChange(activeNodeScriptTab, index, 'initialState', value || '')}
                                      options={{ minimap: { enabled: false }, scrollBeyondLastLine: false, fontSize: 13, tabSize: 2, wordWrap: 'on', automaticLayout: true }}
                                    />
                                  </Box>

                                  {/* UI Renderer */}
                                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>{t('uiRenderer')}</Typography>
                                  <FormHelperText sx={{ mt: -1, mb: 0.5 }}>{t('uiRendererHelper')}</FormHelperText>
                                  <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', mb: 2, overflow: 'hidden' }}>
                                    <Editor
                                      height="300px" width="100%" language="javascript" // jsx is often handled by js parser
                                      theme="vs-dark"
                                      value={editorData.uiRenderer || ''}
                                      onChange={(value) => handleCodeChange(activeNodeScriptTab, index, 'uiRenderer', value || '')}
                                      options={{ minimap: { enabled: false }, scrollBeyondLastLine: false, fontSize: 13, tabSize: 2, wordWrap: 'on', automaticLayout: true }}
                                    />
                                  </Box>

                                  {/* Event Handlers */}
                                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>{t('eventHandlers')}</Typography>
                                  <FormHelperText sx={{ mt: -1, mb: 0.5 }}>{t('eventHandlersHelper')}</FormHelperText>
                                  <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', mb: 2, overflow: 'hidden' }}>
                                    <Editor
                                      height="200px" // Adjust height as needed
                                      width="100%" language="javascript"
                                      theme="vs-dark"
                                      value={editorData.eventHandlers || ''}
                                      onChange={(value) => handleCodeChange(activeNodeScriptTab, index, 'eventHandlers', value || '')}
                                      options={{ minimap: { enabled: false }, scrollBeyondLastLine: false, fontSize: 13, tabSize: 2, wordWrap: 'on', automaticLayout: true }}
                                    />
                                  </Box>


                                  {/* Effects */}
                                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>{t('effects')}</Typography>
                                  <FormHelperText sx={{ mt: -1, mb: 0.5 }}>{t('effectsHelper')}</FormHelperText>
                                  <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', mb: 2, overflow: 'hidden' }}>
                                    <Editor
                                      height="150px" // Adjust height as needed
                                      width="100%" language="javascript"
                                      theme="vs-dark"
                                      value={editorData.effects || ''}
                                      onChange={(value) => handleCodeChange(activeNodeScriptTab, index, 'effects', value || '')}
                                      options={{ minimap: { enabled: false }, scrollBeyondLastLine: false, fontSize: 13, tabSize: 2, wordWrap: 'on', automaticLayout: true }}
                                    />
                                  </Box>



                                </Box>
                              </Box></div> <div className="col-3 py-3" style={{ backgroundColor: '#f9f9f9', borderLeft: '1px solid #ddd', padding: '10px', position: 'sticky', top: 0, height: '60vh', overflowY: 'auto' }}>

                                  <Typography variant="h6" fontWeight={600} gutterBottom>
                                    {t('featureEditorDocsIntro') || 'Use the editors above to define the behavior and appearance of your feature:'}
                                  </Typography>
                                  <ul>
                                    <li><Typography component="span" variant="body2"><strong>{t('initialState')}:</strong> {t('featureEditorDocsInitialState') || 'A JSON object string defining the initial state values.'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><strong>{t('uiRenderer')}:</strong> {t('featureEditorDocsUiRenderer') || 'JSX code for the feature\'s UI. Use MUI components and available scope variables.'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><strong>{t('eventHandlers')}:</strong> {t('featureEditorDocsEventHandlers') || 'A JS object string containing functions to handle UI events (e.g., button clicks, input changes). Use `setState` to update state.'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><strong>{t('effects')}:</strong> {t('featureEditorDocsEffects') || 'A JS array string of effect objects (like `useEffect`) for side effects (e.g., data fetching). Each object needs `effect` (function) and `deps` (array).'}</Typography></li>
                                  </ul>
                                  <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                                    <strong>{t('featureEditorDocsScopeTitle') || 'Available in Scope:'}</strong>
                                  </Typography>
                                  <ul>
                                    <li><Typography component="span" variant="body2"><code style={{ fontWeight: 600 }}>state</code>: {t('featureEditorDocsScopeState') || 'The current state object.'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><code style={{ fontWeight: 600 }}>setState</code>: {t('featureEditorDocsScopeSetState') || 'Function to update the state (e.g., `setState(prevState => ({ ...prevState, key: value }))`).'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><code style={{ fontWeight: 600 }}>handlers</code>: {t('featureEditorDocsScopeHandlers') || 'The object containing your defined event handlers.'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><code style={{ fontWeight: 600 }}>t</code>: {t('featureEditorDocsScopeT') || 'The translation function (`i18next`).'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><code style={{ fontWeight: 600 }}>data</code>: {t('featureEditorDocsScopeData') || 'General module data (like `logoUrl`).'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><code style={{ fontWeight: 600 }}>React</code>: {t('featureEditorDocsScopeReact') || 'The React library itself.'}</Typography></li>
                                    <li><Typography component="span" variant="body2">{t('featureEditorDocsScopeMui') || 'Common Components (e.g., `Box`, `Button`, `TextField`, `Typography`).'}</Typography></li>
                                  </ul>
                                  <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                                    <strong>{t('featureEditorDocsExampleTitle') || 'Example (Simple Counter):'}</strong>
                                  </Typography>

                                  <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 500 }}>{t('initialState')}:</Typography>
                                  <pre style={{ background: '#eee', padding: '8px', borderRadius: '4px', fontSize: '0.8rem', overflowX: 'auto', margin: '4px 0' }}>
                                    {`{
  "count": 0
}`}
                                  </pre>

                                  <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 500 }}>{t('uiRenderer')}:</Typography>
                                  <pre style={{ background: '#eee', padding: '8px', borderRadius: '4px', fontSize: '0.8rem', overflowX: 'auto', margin: '4px 0' }}>
                                    {`<Box>
  <Typography>Count: {state.count}</Typography>
  <Button onClick={handlers.increment}>Increment</Button>
</Box>`}
                                  </pre>

                                  <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 500 }}>{t('eventHandlers')}:</Typography>
                                  <pre style={{ background: '#eee', padding: '8px', borderRadius: '4px', fontSize: '0.8rem', overflowX: 'auto', margin: '4px 0' }}>
                                    {`{
  increment: () => setState(prev => ({ count: prev.count + 1 }))
}`}
                                  </pre>

                                  <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 500 }}>{t('effects')}:</Typography>
                                  <pre style={{ background: '#eee', padding: '8px', borderRadius: '4px', fontSize: '0.8rem', overflowX: 'auto', margin: '4px 0' }}>
                                    {`[
  { effect: () => { console.log('Counter mounted'); }, deps: [] }
]`}
                                  </pre>

                                </div> </div>

                            );
                          }
                          // --- Integration Type JSON Editor ---
                          else if (activeNodeScriptTab === 'integration') {
                            return (
                              <div className='row' key={editorData.id}>
                                <div className='col-9'>
                                  <Box sx={{ border: '1px solid #ddd', borderRadius: '4px', mb: 3, overflow: 'hidden' }}>
                                    <TextField
                                      fullWidth variant="outlined" size="small"
                                      label={`${t(activeNodeScriptTab)} #${index + 1} ${t('title')}`}
                                      value={editorData.title || ''}
                                      onChange={(e) => handleCodeChange(activeNodeScriptTab, index, 'title', e.target.value)}
                                      sx={{
                                        borderBottom: '1px solid #ddd', backgroundColor: '#f9f9f9',
                                        '& .MuiOutlinedInput-root': { borderRadius: '0', '& fieldset': { border: 'none' } },
                                      }}
                                    />
                                    <Editor
                                      height="calc(60vh - 40px)" // Adjust height considering title TextField
                                      width="100%" language="json" theme="vs-dark"
                                      value={editorData.code || '[]'} // Default to empty array string for JSON
                                      onChange={(value) => handleCodeChange(activeNodeScriptTab, index, 'code', value || '[]')}
                                      options={{ minimap: { enabled: false }, scrollBeyondLastLine: false, fontSize: 13, tabSize: 2, wordWrap: 'on', automaticLayout: true }}
                                    />
                                  </Box>
                                </div>
                                <div className="col-3 py-3" style={{ backgroundColor: '#f9f9f9', borderLeft: '1px solid #ddd', padding: '10px', position: 'sticky', top: 0, height: '60vh', overflowY: 'auto' }}>
                                  <Typography variant="h6" fontWeight={600} gutterBottom>
                                    {t('integrationEditorDocsTitle') || 'Integration Instructions'}
                                  </Typography>
                                  <Typography variant="body2" gutterBottom>
                                    {t('integrationEditorDocsIntro') || 'Define a JSON array of instruction objects. Each object specifies an operation and its parameters for the backend to execute.'}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 1 }}><strong>{t('integrationEditorDocsStructure') || 'Structure:'}</strong> <code>{`[{ "operation": "op_name", "params": { ... } }, ...]`}</code ></Typography>

                                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>{t('integrationEditorDocsOperations') || 'Available Operations:'}</Typography>

                                  {/* createFirestoreDoc */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2"><strong>createFirestoreDoc</strong></Typography>
                                    <Typography variant="caption" display="block">{t('integrationEditorDocsCreateDesc') || 'Creates a new document in a Firestore collection.'}</Typography>
                                    <Typography variant="caption" display="block"><strong>{t('params')}:</strong></Typography>
                                    <ul style={{ fontSize: '0.8rem', paddingLeft: '20px', margin: '2px 0' }}>
                                      <li><code>collection</code> (string, required)</li>
                                      <li><code>data</code> (object, required) - Can include <code>timeStamp: "now"</code></li>
                                    </ul>
                                    <pre style={{ background: '#eee', padding: '8px', borderRadius: '4px', fontSize: '0.8rem', overflowX: 'auto', margin: '4px 0' }}>
                                      {`{
  "operation": "createFirestoreDoc",
  "params": {
    "collection": "logs",
    "data": {
      "message": "User logged in",
      "userId": "user123",
      "timeStamp": "now"
    }
  }
}`}
                                    </pre>
                                  </Box>

                                  {/* updateFirestoreDoc */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2"><strong>updateFirestoreDoc</strong></Typography>
                                    <Typography variant="caption" display="block">{t('integrationEditorDocsUpdateDesc') || 'Updates an existing document in Firestore.'}</Typography>
                                    <Typography variant="caption" display="block"><strong>{t('params')}:</strong></Typography>
                                    <ul style={{ fontSize: '0.8rem', paddingLeft: '20px', margin: '2px 0' }}>
                                      <li><code>collection</code> (string, required)</li>
                                      <li><code>docId</code> (string, required)</li>
                                      <li><code>data</code> (object, required) - Fields to update.</li>
                                    </ul>
                                    <pre style={{ background: '#eee', padding: '8px', borderRadius: '4px', fontSize: '0.8rem', overflowX: 'auto', margin: '4px 0' }}>
                                      {`{
  "operation": "updateFirestoreDoc",
  "params": {
    "collection": "profiles",
    "docId": "user123",
    "data": {
      "lastLogin": "now",
      "status": "active"
    }
  }
}`}
                                    </pre>
                                  </Box>

                                  {/* deleteFirestoreDoc */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2"><strong>deleteFirestoreDoc</strong></Typography>
                                    <Typography variant="caption" display="block">{t('integrationEditorDocsDeleteDesc') || 'Deletes a document from Firestore.'}</Typography>
                                    <Typography variant="caption" display="block"><strong>{t('params')}:</strong></Typography>
                                    <ul style={{ fontSize: '0.8rem', paddingLeft: '20px', margin: '2px 0' }}>
                                      <li><code>collection</code> (string, required)</li>
                                      <li><code>docId</code> (string, required)</li>
                                    </ul>
                                    <pre style={{ background: '#eee', padding: '8px', borderRadius: '4px', fontSize: '0.8rem', overflowX: 'auto', margin: '4px 0' }}>
                                      {`{
  "operation": "deleteFirestoreDoc",
  "params": {
    "collection": "sessions",
    "docId": "sessionXYZ"
  }
}`}
                                    </pre>
                                  </Box>

                                  {/* fetchAPI */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2"><strong>fetchAPI</strong></Typography>
                                    <Typography variant="caption" display="block">{t('integrationEditorDocsFetchDesc') || 'Makes an HTTP request to an external API.'}</Typography>
                                    <Typography variant="caption" display="block"><strong>{t('params')}:</strong></Typography>
                                    <ul style={{ fontSize: '0.8rem', paddingLeft: '20px', margin: '2px 0' }}>
                                      <li><code>url</code> (string, required)</li>
                                      <li><code>method</code> (string, optional, default: 'GET')</li>
                                      <li><code>headers</code> (object, optional)</li>
                                      <li><code>body</code> (object, optional)</li>
                                      <li><code>query</code> (object, optional)</li>
                                    </ul>
                                    <pre style={{ background: '#eee', padding: '8px', borderRadius: '4px', fontSize: '0.8rem', overflowX: 'auto', margin: '4px 0' }}>
                                      {`{
  "operation": "fetchAPI",
  "params": {
    "url": "https://api.example.com/data",
    "method": "POST",
    "headers": {
      "Authorization": "Bearer your_token",
      "Content-Type": "application/json"
    },
    "body": {
      "key": "value"
    }
  }
}`}
                                    </pre>
                                  </Box>

                                  {/* incrementCounter */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2"><strong>incrementCounter</strong></Typography>
                                    <Typography variant="caption" display="block">{t('integrationEditorDocsIncrementDesc') || 'Atomically increments a numeric field in Firestore.'}</Typography>
                                    <Typography variant="caption" display="block"><strong>{t('params')}:</strong></Typography>
                                    <ul style={{ fontSize: '0.8rem', paddingLeft: '20px', margin: '2px 0' }}>
                                      <li><code>collection</code> (string, required)</li>
                                      <li><code>docId</code> (string, required)</li>
                                      <li><code>field</code> (string, required) - The field to increment.</li>
                                      <li><code>amount</code> (number, optional, default: 1)</li>
                                    </ul>
                                    <pre style={{ background: '#eee', padding: '8px', borderRadius: '4px', fontSize: '0.8rem', overflowX: 'auto', margin: '4px 0' }}>
                                      {`{
  "operation": "incrementCounter",
  "params": {
    "collection": "stats",
    "docId": "apiUsage",
    "field": "requests",
    "amount": 1
  }
}`}
                                    </pre>
                                  </Box>

                                </div>
                              </div>
                            );
                          }
                          // --- Component Type Structured Editor (similar to Feature) ---
                          else if (activeNodeScriptTab === 'component') {
                            return (
                              <div className='row'><div className='col-9'> <Box key={editorData.id} sx={{ border: '1px solid #ddd', borderRadius: '4px', mb: 3, overflow: 'hidden' }}>
                                <TextField
                                  fullWidth variant="outlined" size="small"
                                  label={`${t('component')} #${index + 1} ${t('title')}`} // Changed label
                                  value={editorData.title || ''}
                                  onChange={(e) => handleCodeChange(activeNodeScriptTab, index, 'title', e.target.value)}
                                  sx={{ backgroundColor: '#f9f9f9', borderBottom: '1px solid #ddd', '& .MuiOutlinedInput-root': { borderRadius: '0', '& fieldset': { border: 'none' } } }}
                                />
                                <Box sx={{ p: 2 }}>
                                  {/* Initial State */}
                                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>{t('initialState')}</Typography>
                                  <FormHelperText sx={{ mt: -1, mb: 0.5 }}>{t('initialStateHelper')}</FormHelperText>
                                  <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', mb: 2, overflow: 'hidden' }}>
                                    <Editor
                                      height="150px"
                                      width="100%" language="javascript"
                                      theme="vs-dark"
                                      value={editorData.initialState || ''}
                                      onChange={(value) => handleCodeChange(activeNodeScriptTab, index, 'initialState', value || '')}
                                      options={{ minimap: { enabled: false }, scrollBeyondLastLine: false, fontSize: 13, tabSize: 2, wordWrap: 'on', automaticLayout: true }}
                                    />
                                  </Box>

                                  {/* UI Renderer */}
                                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>{t('uiRenderer')}</Typography>
                                  <FormHelperText sx={{ mt: -1, mb: 0.5 }}>{t('uiRendererHelper')}</FormHelperText>
                                  <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', mb: 2, overflow: 'hidden' }}>
                                    <Editor
                                      height="300px" width="100%" language="javascript"
                                      theme="vs-dark"
                                      value={editorData.uiRenderer || ''}
                                      onChange={(value) => handleCodeChange(activeNodeScriptTab, index, 'uiRenderer', value || '')}
                                      options={{ minimap: { enabled: false }, scrollBeyondLastLine: false, fontSize: 13, tabSize: 2, wordWrap: 'on', automaticLayout: true }}
                                    />
                                  </Box>

                                  {/* Event Handlers */}
                                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>{t('eventHandlers')}</Typography>
                                  <FormHelperText sx={{ mt: -1, mb: 0.5 }}>{t('eventHandlersHelper')}</FormHelperText>
                                  <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', mb: 2, overflow: 'hidden' }}>
                                    <Editor
                                      height="200px"
                                      width="100%" language="javascript"
                                      theme="vs-dark"
                                      value={editorData.eventHandlers || ''}
                                      onChange={(value) => handleCodeChange(activeNodeScriptTab, index, 'eventHandlers', value || '')}
                                      options={{ minimap: { enabled: false }, scrollBeyondLastLine: false, fontSize: 13, tabSize: 2, wordWrap: 'on', automaticLayout: true }}
                                    />
                                  </Box>

                                  {/* Effects */}
                                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>{t('effects')}</Typography>
                                  <FormHelperText sx={{ mt: -1, mb: 0.5 }}>{t('effectsHelper')}</FormHelperText>
                                  <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', mb: 2, overflow: 'hidden' }}>
                                    <Editor
                                      height="150px"
                                      width="100%" language="javascript"
                                      theme="vs-dark"
                                      value={editorData.effects || ''}
                                      onChange={(value) => handleCodeChange(activeNodeScriptTab, index, 'effects', value || '')}
                                      options={{ minimap: { enabled: false }, scrollBeyondLastLine: false, fontSize: 13, tabSize: 2, wordWrap: 'on', automaticLayout: true }}
                                    />
                                  </Box>
                                </Box>
                              </Box></div> <div className="col-3 py-3" style={{ backgroundColor: '#f9f9f9', borderLeft: '1px solid #ddd', padding: '10px', position: 'sticky', top: 0, height: '60vh', overflowY: 'auto' }}>

                                  <Typography variant="h6" fontWeight={600} gutterBottom>
                                    {t('componentEditorDocsTitle') || 'Component Editor Documentation'} // Changed title
                                  </Typography>
                                  <Typography variant="body2" gutterBottom>
                                    {t('componentEditorDocsIntro') || 'Use the editors above to define the behavior and appearance of your component:'} // Changed intro
                                  </Typography>
                                  <ul>
                                    <li><Typography component="span" variant="body2"><strong>{t('initialState')}:</strong> {t('componentEditorDocsInitialState') || 'A JSON object string defining the initial state values.'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><strong>{t('uiRenderer')}:</strong> {t('componentEditorDocsUiRenderer') || 'JSX code for the component\'s UI. Use MUI components and available scope variables.'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><strong>{t('eventHandlers')}:</strong> {t('componentEditorDocsEventHandlers') || 'A JS object string containing functions to handle UI events. Use `setState` to update state.'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><strong>{t('effects')}:</strong> {t('componentEditorDocsEffects') || 'A JS array string of effect objects (like `useEffect`) for side effects.'}</Typography></li>
                                  </ul>
                                  <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                                    <strong>{t('componentEditorDocsScopeTitle') || 'Available in Scope:'}</strong> // Changed scope title
                                  </Typography>
                                  <ul>
                                    <li><Typography component="span" variant="body2"><code style={{ fontWeight: 600 }}>state</code>: {t('componentEditorDocsScopeState') || 'The current state object.'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><code style={{ fontWeight: 600 }}>setState</code>: {t('componentEditorDocsScopeSetState') || 'Function to update the state.'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><code style={{ fontWeight: 600 }}>handlers</code>: {t('componentEditorDocsScopeHandlers') || 'The object containing your defined event handlers.'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><code style={{ fontWeight: 600 }}>t</code>: {t('componentEditorDocsScopeT') || 'The translation function (`i18next`).'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><code style={{ fontWeight: 600 }}>data</code>: {t('componentEditorDocsScopeData') || 'General module data (like `logoUrl`).'}</Typography></li>
                                    <li><Typography component="span" variant="body2"><code style={{ fontWeight: 600 }}>React</code>: {t('componentEditorDocsScopeReact') || 'The React library itself.'}</Typography></li>
                                    <li><Typography component="span" variant="body2">{t('componentEditorDocsScopeMui') || 'Common Components (e.g., `Box`, `Button`, `TextField`, `Typography`).'}</Typography></li>
                                  </ul>
                                  <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                                    <strong>{t('componentEditorDocsExampleTitle') || 'Example (Simple Counter):'}</strong> // Example title can stay generic
                                  </Typography>

                                  <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 500 }}>{t('initialState')}:</Typography>
                                  <pre style={{ background: '#eee', padding: '8px', borderRadius: '4px', fontSize: '0.8rem', overflowX: 'auto', margin: '4px 0' }}>
                                    {`{
  "count": 0
}`}
                                  </pre>

                                  <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 500 }}>{t('uiRenderer')}:</Typography>
                                  <pre style={{ background: '#eee', padding: '8px', borderRadius: '4px', fontSize: '0.8rem', overflowX: 'auto', margin: '4px 0' }}>
                                    {`<Box>
  <Typography>Count: {state.count}</Typography>
  <Button onClick={handlers.increment}>Increment</Button>
</Box>`}
                                  </pre>

                                  <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 500 }}>{t('eventHandlers')}:</Typography>
                                  <pre style={{ background: '#eee', padding: '8px', borderRadius: '4px', fontSize: '0.8rem', overflowX: 'auto', margin: '4px 0' }}>
                                    {`{
  increment: () => setState(prev => ({ count: prev.count + 1 }))
}`}
                                  </pre>

                                  <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 500 }}>{t('effects')}:</Typography>
                                  <pre style={{ background: '#eee', padding: '8px', borderRadius: '4px', fontSize: '0.8rem', overflowX: 'auto', margin: '4px 0' }}>
                                    {`[
  { effect: () => { console.log('Counter mounted'); }, deps: [] }
]`}
                                  </pre>

                                </div> </div>

                            );
                          }
                          // --- Default Editor for other types ---
                          else {
                            return (
                              <Box key={editorData.id} sx={{ mt: 1, border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden', mb: 2 }}>
                                <TextField
                                  fullWidth variant="outlined" size="small"
                                  label={`${t(activeNodeScriptTab)} #${index + 1} ${t('title')}`}
                                  value={editorData.title}
                                  onChange={(e) => handleCodeChange(activeNodeScriptTab, index, 'title', e.target.value)}
                                  sx={{
                                    borderBottom: '1px solid #ddd', backgroundColor: '#f9f9f9',
                                    '& .MuiOutlinedInput-root': { borderRadius: '0', '& fieldset': { border: 'none' }, '&:hover fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: 'none' } },
                                    '& .MuiInputLabel-root': { fontSize: '0.8rem' }
                                  }}
                                />
                                <Editor
                                  height="400px" // Adjust height as needed
                                  width="100%" language="javascript" theme="vs-dark"
                                  value={editorData.code} // Use 'code' field for non-feature types
                                  onChange={(value) => handleCodeChange(activeNodeScriptTab, index, 'code', value || '')}
                                  options={{ minimap: { enabled: false }, scrollBeyondLastLine: false, fontSize: 13, tabSize: 2, wordWrap: 'on', automaticLayout: true }}
                                />
                              </Box>
                            );
                          }
                        })}
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography color="text.secondary">{t('noCodeEditorTypesSelected')}</Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={handleCloseDialog} color="secondary" disabled={isLoading}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSaveModule} color="primary" variant="contained" disabled={isLoading || !formValues.title}>
            {isLoading ? t('saving') + '...' : t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deletion Confirmation Dialog */}
      <Dialog open={!!pendingDeletionInfo} onClose={handleCancelDeletion} maxWidth="sm" fullWidth>
        <DialogTitle>{t('confirmDeletionTitle')}</DialogTitle>
        <DialogContent>
          {pendingDeletionInfo && (
            <>
              <Typography gutterBottom>
                {t('confirmDeletionPrompt', {
                  count: pendingDeletionInfo.countToRemove,
                  type: t(pendingDeletionInfo.typeValue)
                })}
              </Typography>
              <List dense>
                {pendingDeletionInfo.candidates.map(candidate => (
                  <ListItem key={candidate.id} dense>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedForDeletion.includes(candidate.id)}
                          onChange={(e) => handleDeletionSelectionChange(e, candidate.id)}
                          disabled={
                            selectedForDeletion.length >= pendingDeletionInfo.countToRemove &&
                            !selectedForDeletion.includes(candidate.id)
                          }
                        />
                      }
                      label={candidate.title || t('untitled')}
                      sx={{ width: '100%' }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeletion} color="secondary">
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirmDeletion}
            color="error"
            variant="contained"
            disabled={!pendingDeletionInfo || selectedForDeletion.length !== pendingDeletionInfo.countToRemove}
          >
            {t('confirmDelete')}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayoutV2>
  );
};

export default MarketPlaceNode;